import {
  ClipboardCheck,
  Star,
  Loader2,
  Calendar,
  Trophy,
  AlertCircle,
} from "lucide-react";
import { useAssessment } from "@/hooks/assessment";
import { formatDate, getLevelStyle } from "@/data/assessment";

const Assessment: React.FC = () => {
  const { assessments, isLoading, error } = useAssessment();

  if (error) {
    return (
      <div className="min-h-[50vh] flex flex-col justify-center items-center text-red-500 gap-3">
        <AlertCircle size={48} className="opacity-80" />
        <p className="font-bold text-lg">An Error Occurred</p>
        <p className="text-sm bg-red-50 px-4 py-2 rounded-lg">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="text-sm underline hover:text-red-700"
        >
          Try Reloading
        </button>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 py-10 font-poppins bg-[#f9fafb] min-h-screen">
      {/* Header Section */}
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-[#1A4D57]">
            Assessment History
          </h2>
          <p className="text-gray-500 mt-1">
            Track your language proficiency progress here.
          </p>
        </div>

        {/* Short Stats (Optional) */}
        {!isLoading && assessments.length > 0 && (
          <div className="bg-white px-5 py-3 rounded-xl shadow-sm border border-gray-100 flex items-center gap-3">
            <div className="bg-[#C4A603]/10 p-2 rounded-lg">
              <Trophy size={20} className="text-[#C4A603]" />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-bold uppercase">
                Latest Test
              </p>
              <p className="text-sm font-bold text-[#1A4D57]">
                {assessments[0].level}
              </p>
            </div>
          </div>
        )}
      </div>

      {!isLoading && assessments.length === 0 ? (
        <div className="w-full bg-[#E8F5E9] rounded-2xl p-6 md:p-8">
          <p className="text-gray-800 font-medium text-sm md:text-base leading-relaxed text-left">
            If there are any technical or system issues, please contact the
            assigned class trainer or the Fluenesia admin.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-[24px] overflow-hidden shadow-lg shadow-gray-100/50">
          <div className="overflow-x-auto">
            <table className="w-full text-left whitespace-nowrap">
              <thead className="bg-[#1A4D57]/5 border-b border-[#1A4D57]/10">
                <tr>
                  <th className="px-6 py-5 text-[11px] font-black text-[#1A4D57] uppercase tracking-widest">
                    Test Title
                  </th>
                  <th className="px-6 py-5 text-[11px] font-black text-[#1A4D57] uppercase tracking-widest">
                    Date Taken
                  </th>
                  <th className="px-6 py-5 text-[11px] font-black text-[#1A4D57] uppercase tracking-widest text-center">
                    Score
                  </th>
                  <th className="px-6 py-5 text-[11px] font-black text-[#1A4D57] uppercase tracking-widest text-center">
                    Level Achieved
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-6 py-20 text-center text-gray-400"
                    >
                      <div className="flex flex-col justify-center items-center gap-3">
                        <Loader2
                          size={32}
                          className="animate-spin text-[#1A4D57]"
                        />
                        <span className="text-sm font-medium">
                          Loading history data...
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  assessments.map((item, index) => {
                    const style = getLevelStyle(item.level);
                    return (
                      <tr
                        key={item.id || index}
                        className="hover:bg-gray-50 transition duration-200"
                      >
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-4">
                            <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                              <ClipboardCheck size={20} />
                            </div>
                            <span className="font-bold text-gray-800 text-sm">
                              {item.quizTitle || "Placement Test"}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-2 text-gray-500 text-sm">
                            <Calendar size={14} />
                            {formatDate(item.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-5 text-center">
                          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-50 text-orange-600 font-bold border border-orange-100">
                            <Star size={14} fill="currentColor" />
                            <span className="text-sm">{item.score}</span>
                          </div>
                        </td>
                        <td className="px-6 py-5 text-center">
                          <span
                            className={`text-[11px] font-bold px-4 py-1.5 rounded-full uppercase border tracking-wide ${style.bg} ${style.text} ${style.border}`}
                          >
                            {item.level || "UNRANKED"}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Assessment;
